import { Typography } from '@mui/material'
import React, { Fragment, memo, useCallback, useMemo } from 'react'
import { addS } from 'src/utils/baseUtils'
import tw, { css } from 'twin.macro'
import CheckIcon from '../../../assets/icons/checkmark.svg'
import { InfoMultilineContainer } from 'src/components/shared/InfoMultilineContainer'
import { useSelector } from 'react-redux'
import { CollectDataConfig, VehicleInsuranceCollectDataStep } from 'src/interfaces/collectData'
import { StepItem } from './StepItem'
import { CollectDataConfigStatus, CollectDataStepType } from 'src/constants/collect-data/enums'
import {
  COLLECT_DATA_STEP_TIME_TO_COMPLETE,
  collectDataStepDataMap,
} from 'src/constants/collect-data/constants'
import { ChevronUpMinor } from '@shopify/polaris-icons'
import { getPartner } from 'src/selectors/partner'

interface Props {
  index: number
  collectDataConfig: CollectDataConfig
  disabled?: boolean
}

const StepConfigItemComponent = ({ index, collectDataConfig, disabled }: Props) => {
  const partner = useSelector(getPartner)

  const [isCollapsed, setIsCollapsed] = React.useState(true)

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev)
  }, [])

  const delayedDescription = useMemo(
    () => partner?.gigsafeConfig?.onboardingDelayedStepDescription,
    [partner?.gigsafeConfig?.onboardingDelayedStepDescription],
  )

  const completed = collectDataConfig.status === CollectDataConfigStatus.Completed
  const finishTime = collectDataConfig.collectDataSteps.reduce((acc, item) => {
    acc += COLLECT_DATA_STEP_TIME_TO_COMPLETE[item.type] || 0

    return acc
  }, 0)
  const isSingleStep = collectDataConfig.collectDataSteps.length === 1
  const title = useMemo(() => {
    if (isSingleStep) {
      const firstStep = collectDataConfig.collectDataSteps[0]

      if (!firstStep) {
        return 'Step'
      }

      let stepTitle =
        collectDataStepDataMap[firstStep.type]({
          optional: firstStep.optional,
        }).title || 'Step'

      if (firstStep.type === CollectDataStepType.VehicleInsurance) {
        const vehicleInsuranceStep = firstStep as VehicleInsuranceCollectDataStep

        if (vehicleInsuranceStep.metadata?.disableInsuranceUpload) {
          stepTitle = 'Vehicle Information'
        }
      }

      return stepTitle
    }

    return collectDataConfig.title || `Step ${index}`
  }, [isSingleStep, collectDataConfig.collectDataSteps, collectDataConfig.title, index])

  return (
    <Fragment>
      {disabled ? (
        <InfoMultilineContainer
          className={tw`mb-4`}
          title={partner?.gigsafeConfig?.onboardingDelayedStepTitle || 'Wait for Approval'}
        >
          {delayedDescription ? (
            <div
              css={styles.descriptionContainer}
              dangerouslySetInnerHTML={{
                __html: delayedDescription,
              }}
            />
          ) : (
            'We will review your documents and notify you of the decision via email within 3 business days. If approved, you can proceed to the next steps.'
          )}
        </InfoMultilineContainer>
      ) : null}
      <div
        css={tw`inline-flex flex-col items-start gap-[8px] p-4 bg-shades-0 rounded-[16px] overflow-hidden w-full min-h-[80px] mb-4`}
      >
        <div
          css={tw`flex flex-row items-center justify-between w-full cursor-pointer`}
          className="group"
          onClick={toggleCollapsed}
          data-testid={`contractors-onboarding-steps-row-${collectDataConfig.id}`}
        >
          <div css={tw`flex flex-row items-center`}>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mr-4`}
            >
              {completed ? (
                <img src={CheckIcon} />
              ) : (
                <div css={tw`bg-black w-5 h-5 rounded-full flex justify-center items-center`}>
                  <Typography variant="subtitle2" css={tw`text-shades-100`}>
                    {index}
                  </Typography>
                </div>
              )}
            </div>
            <div css={tw`flex flex-col gap-[2px]`}>
              <Typography variant="h4">{title}</Typography>
              {completed ? (
                <Typography variant="caption" css={tw`text-success-500`}>
                  Completed
                </Typography>
              ) : (
                <Typography variant="caption" css={tw`text-primary-500`}>
                  {`${
                    !isSingleStep ? `${collectDataConfig.collectDataSteps.length} Parts • ` : ''
                  }Finish in ${finishTime} ${addS(finishTime, 'minute')}`}
                </Typography>
              )}
            </div>
          </div>
          <ChevronUpMinor
            width={20}
            css={isCollapsed && tw`rotate-180`}
            className="group-hover:opacity-40"
          />
        </div>
        {!isCollapsed && (
          <div css={tw`w-full rounded-lg overflow-hidden border border-solid border-shades-100`}>
            {collectDataConfig.collectDataSteps.map((item, index) => {
              return (
                <Fragment key={item.id}>
                  {!isSingleStep && <div css={tw`mt-2`} />}
                  <StepItem
                    collectDataStep={item}
                    partIndex={!isSingleStep ? index + 1 : undefined}
                    className={
                      index < collectDataConfig.collectDataSteps.length - 1 &&
                      tw`border-0 border-b border-solid border-shades-100`
                    }
                  />
                </Fragment>
              )
            })}
          </div>
        )}
      </div>
    </Fragment>
  )
}

export const StepConfigItem = memo(StepConfigItemComponent)

const styles = {
  descriptionContainer: css`
    > p {
      margin-top: 8px;
      margin-bottom: 8px;
    }
    > h4 {
      margin-top: 8px;
      margin-bottom: 8px;
    }
    > a {
      text-decoration-color: rgba(127, 0, 255, 1);
      color: rgba(127, 0, 255, 1);
    }
    > a:hover {
      text-decoration-color: rgba(48, 2, 88, 1);
      color: rgba(48, 2, 88, 1);
    }
  `,
}
